/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useLexicalEditable } from "@lexical/react/useLexicalEditable";
import { mergeRegister } from "@lexical/utils";
import {
  $getNodeByKey,
  $getSelection,
  $isNodeSelection,
  COMMAND_PRIORITY_HIGH,
  isHTMLElement,
  KEY_ESCAPE_COMMAND,
  NodeKey,
  SELECTION_CHANGE_COMMAND,
} from "lexical";
import * as React from "react";
import { ChangeEvent, forwardRef, JSX, Ref, RefObject, useCallback, useEffect, useRef, useState } from "react";
import { useWatchDependency } from "@sinch/hooks/utilities/useWatchDependency";

import { $isPlaceholderNode } from "./PlaceholderNode";

interface PlaceholderComponentProps {
  placeholder: string;
  nodeKey: NodeKey;
}

export function PlaceholderComponent({ placeholder, nodeKey }: PlaceholderComponentProps): JSX.Element {
  const [editor] = useLexicalComposerContext();
  const isEditable = useLexicalEditable();
  const [placeholderValue, setPlaceholderValue] = useState(placeholder);
  const [showPlaceholderEditor, setShowPlaceholderEditor] = useState<boolean>(false);
  const inputRef = useRef(null);
  const onHide = useCallback(
    (restoreSelection?: boolean) => {
      setShowPlaceholderEditor(false);
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if ($isPlaceholderNode(node)) {
          node.setPlaceholder(placeholderValue);
          if (restoreSelection) {
            node.selectNext(0, 0);
          }
        }
      });
    },
    [editor, placeholderValue, nodeKey]
  );

  // Keep the local value in sync with the prop while the editor is closed.
  useWatchDependency(() => {
    if (!showPlaceholderEditor && placeholderValue !== placeholder) {
      setPlaceholderValue(placeholder);
    }
  }, [showPlaceholderEditor, placeholder, placeholderValue]);

  useEffect(() => {
    if (!isEditable) {
      return;
    }
    if (showPlaceholderEditor) {
      return mergeRegister(
        editor.registerCommand(
          SELECTION_CHANGE_COMMAND,
          () => {
            const activeElement = document.activeElement;
            const inputElem = inputRef.current;
            if (inputElem !== activeElement) {
              onHide();
            }
            return false;
          },
          COMMAND_PRIORITY_HIGH
        ),
        editor.registerCommand(
          KEY_ESCAPE_COMMAND,
          () => {
            const activeElement = document.activeElement;
            const inputElem = inputRef.current;
            if (inputElem === activeElement) {
              onHide(true);
              return true;
            }
            return false;
          },
          COMMAND_PRIORITY_HIGH
        )
      );
    } else {
      return editor.registerUpdateListener(({ editorState }) => {
        const isSelected = editorState.read(() => {
          const selection = $getSelection();
          return $isNodeSelection(selection) && selection.has(nodeKey) && selection.getNodes().length === 1;
        });
        if (isSelected) {
          setShowPlaceholderEditor(true);
        }
      });
    }
  }, [editor, nodeKey, onHide, showPlaceholderEditor, isEditable]);
  return (
    <>
      {showPlaceholderEditor && isEditable ? (
        <span style={{ backgroundColor: "#EDEDED" }}>[[{placeholderValue}]]</span>
      ) : (
        <span>[[{placeholderValue}]]</span>
      )}
    </>
  );
  // TODO: editable placeholder
  /*return (
    <>
      {showPlaceholderEditor && isEditable ? (
        <PlaceholderEditor placeholder={placeholderValue} setPlaceholder={setPlaceholderValue} ref={inputRef} />
      ) : (
        <span>[[{placeholderValue}]]</span>
      )}
    </>
  );*/
}

interface BasePlaceholderEditorProps {
  placeholder: string;
  setPlaceholder: (placeholder: string) => void;
}

function PlaceholderEditorBase(
  { placeholder, setPlaceholder }: BasePlaceholderEditorProps,
  forwardedRef: Ref<HTMLInputElement | HTMLTextAreaElement>
): JSX.Element | null {
  const onChange = (event: ChangeEvent) => {
    setPlaceholder((event.target as HTMLInputElement).value);
  };

  // isHTMLElement is a type guard that inspects the value itself, it does not read .current
  // oxlint-disable-next-line react-hooks-js/refs
  return isHTMLElement(forwardedRef) ? (
    <span className="PlaceholderEditor_inputBackground">
      <span className="PlaceholderEditor_dollarSign">[[</span>
      <input
        ref={forwardedRef as RefObject<HTMLInputElement>}
        autoFocus
        className="PlaceholderEditor_inlineEditor"
        onChange={onChange}
        value={placeholder}
      />
      <span className="PlaceholderEditor_dollarSign">]]</span>
    </span>
  ) : null;
}

export const PlaceholderEditor = forwardRef(PlaceholderEditorBase);
